import _slicedToArray from "@babel/runtime/helpers/slicedToArray";

/**
 * @file Modal.tsx
 * @author markJia(markjia@tencent.com)
 */
import React, { Fragment, useMemo, useRef, useLayoutEffect, forwardRef, useImperativeHandle } from 'react';
import { useLockScreen } from '@rc-hooks/dom';
import { useUncontrolled, useCombineCallback } from '@tencent/glue-react-use';
import { RcDialog, RcDialogHorizontalType, RcDialogVerticalType } from '../../units/RcDialog';
import { ViewContent } from '../../views/ViewContent';
import { usePrefixCls } from '../../hooks/usePrefix';

var useIsLazyShow = function useIsLazyShow() {
  var lazyInit = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
  var isShow = arguments.length > 1 ? arguments[1] : undefined;
  var canLazyShowRef = useRef(!lazyInit);
  return useMemo(function () {
    if (isShow) {
      canLazyShowRef.current = true;
    }

    return canLazyShowRef.current;
  }, [isShow]);
};

var getDialogVertical = function getDialogVertical(top) {
  if (top != null) {
    return {
      vertical: RcDialogVerticalType.TOP,
      top: top
    };
  }

  return {
    vertical: RcDialogVerticalType.MIDDLE
  };
};

var horizontal = {
  horizontal: RcDialogHorizontalType.CENTER
};
export var Modal = forwardRef(function (props, ref) {
  var defaultShow = props.defaultShow,
      show = props.show,
      onVisibleChange = props.onVisibleChange,
      _props$lazyInit = props.lazyInit,
      lazyInit = _props$lazyInit === void 0 ? true : _props$lazyInit,
      top = props.top,
      hideToDestroy = props.hideToDestroy,
      header = props.header,
      title = props.title,
      footer = props.footer,
      closeIcon = props.closeIcon,
      _props$mask = props.mask,
      mask = _props$mask === void 0 ? true : _props$mask,
      _props$maskClassName = props.maskClassName,
      maskClassName = _props$maskClassName === void 0 ? '' : _props$maskClassName,
      maskStyle = props.maskStyle,
      _props$maskClosable = props.maskClosable,
      maskClosable = _props$maskClosable === void 0 ? true : _props$maskClosable,
      _props$okText = props.okText,
      okText = _props$okText === void 0 ? '确定' : _props$okText,
      _props$cancelText = props.cancelText,
      cancelText = _props$cancelText === void 0 ? '取消' : _props$cancelText,
      onOk = props.onOk,
      width = props.width,
      onCancel = props.onCancel,
      okProps = props.okProps,
      cancelProps = props.cancelProps,
      _props$closable = props.closable,
      closable = _props$closable === void 0 ? true : _props$closable,
      getContainer = props.getContainer,
      _props$modalClassName = props.modalClassName,
      modalClassName = _props$modalClassName === void 0 ? '' : _props$modalClassName,
      modalStyle = props.modalStyle,
      _props$modalContentCl = props.modalContentClassName,
      modalContentClassName = _props$modalContentCl === void 0 ? '' : _props$modalContentCl,
      modalContentStyle = props.modalContentStyle,
      children = props.children,
      zIndex = props.zIndex,
      _props$headerClassNam = props.headerClassName,
      headerClassName = _props$headerClassNam === void 0 ? '' : _props$headerClassNam,
      headerStyle = props.headerStyle,
      _props$bodyClassName = props.bodyClassName,
      bodyClassName = _props$bodyClassName === void 0 ? '' : _props$bodyClassName,
      bodyStyle = props.bodyStyle,
      _props$footerClassNam = props.footerClassName,
      footerClassName = _props$footerClassNam === void 0 ? '' : _props$footerClassNam,
      footerStyle = props.footerStyle;

  var _useUncontrolled = useUncontrolled(typeof show === 'boolean', false, show, defaultShow, onVisibleChange, 'show'),
      _useUncontrolled2 = _slicedToArray(_useUncontrolled, 2),
      isShow = _useUncontrolled2[0],
      onShowChange = _useUncontrolled2[1];

  useImperativeHandle(ref, function () {
    return {
      isShow: isShow,
      setShow: onShowChange
    };
  }, [onShowChange]);

  var _useLockScreen = useLockScreen(),
      lockScreen = _useLockScreen.lockScreen,
      unlockScreen = _useLockScreen.unlockScreen; // 避免抖动


  useLayoutEffect(function () {
    return isShow ? lockScreen() : unlockScreen();
  }, [isShow]);
  var cls = usePrefixCls('modal-root');
  var canLazyShow = useIsLazyShow(lazyInit, isShow);
  var vertical = useMemo(function () {
    return getDialogVertical(top);
  }, [top]);
  var cancelHandle = useCombineCallback(onCancel, function () {
    return onShowChange(false);
  });

  if (!canLazyShow || hideToDestroy && !isShow) {
    return React.createElement(Fragment, null);
  }

  return React.createElement(RcDialog, {
    show: isShow,
    className: "".concat(cls, " ").concat(modalClassName),
    style: modalStyle,
    zIndex: zIndex,
    vertical: vertical,
    horizontal: horizontal,
    mask: mask,
    maskClassName: maskClassName,
    maskStyle: maskStyle,
    maskClosable: maskClosable,
    maskClick: maskClosable ? cancelHandle : undefined,
    getContainer: getContainer
  }, React.createElement(ViewContent, {
    className: modalContentClassName,
    style: modalContentStyle,
    closable: closable,
    closeIcon: closeIcon,
    onOk: onOk,
    width: width,
    okProps: okProps,
    cancelProps: cancelProps,
    okText: okText,
    title: title,
    onCancel: cancelHandle,
    cancelText: cancelText,
    header: header,
    headerClassName: headerClassName,
    headerStyle: headerStyle,
    bodyClassName: bodyClassName,
    bodyStyle: bodyStyle,
    footer: footer,
    footerClassName: footerClassName,
    footerStyle: footerStyle
  }, children));
});